/**
 * @component ry-card
 * @category  display
 * @summary   Block-level content container with chrome (border, radius, padding, optional shadow). Maps to Fomantic UI's `.ui.card` AND `.ui.segment` — ry-ui collapses both into one primitive. Pure layout by default; opt into link-like behavior with `interactive` (and optionally `href`).
 *
 * Reference: Fomantic UI `.ui.card` and `.ui.segment`. The two are merged here
 * because their visual purpose overlaps; pick a theme variant (raised, basic,
 * fluid, etc.) to match the visual you need. Interactive cards behave like
 * anchors — keyboard activation, click navigation, and ARIA role="link".
 *
 * @attribute interactive   boolean   default=false   Enables click/Enter/Space activation. The card receives `tabindex="0"` (unless explicitly set) and `role="link"`. Without this attribute the card is purely decorative.
 * @attribute href          string?                   When set together with `interactive`, navigates `window.location.href` to the value on activation. The `ry:click` event still fires — listeners run before navigation in the same task, but synchronous redirect may interrupt.
 *
 * @event ry:click
 *        detail: { originalEvent: MouseEvent | KeyboardEvent }
 *        fires:  on click or keyboard activation of an `interactive` card, but ONLY when the click did NOT originate from an `<a>`, `<button>`, or `<ry-button>` descendant. Inner interactive elements take precedence — the card's click is suppressed so nested controls keep their semantics.
 *
 * @keyboard  (only when [interactive]) Enter / Space — activate the card; preventDefault on Space to avoid page scroll. Synthesizes a click via `this.click()` which routes through `#handleClick`.
 *
 * @content   Any HTML. Cards typically contain `<img>`, headings, paragraphs, and a footer action area. Nested `<a>`, `<button>`, or `<ry-button>` work normally; their clicks do NOT bubble up to the card's interactive handler.
 *
 * @dom
 *   ry-card[interactive?][href?]   role="link"?  tabindex="0"?
 *     > {arbitrary children}
 *     (no internal transform — the card is a plain container)
 *
 * @gotcha  By default (no `interactive`) the element has no event listeners, no tabindex, no role. It is purely a styling hook. Don't expect clicks to fire `ry:click` unless `interactive` is set.
 *
 * @gotcha  Inner click suppression matches `<a>`, `<button>`, and `<ry-button>` via `closest()`. Other interactive elements (`<input>`, custom controls) do NOT trigger suppression — their clicks bubble and fire `ry:click` on the card. Wrap them in a non-card region or check the event target in your listener.
 *
 * @gotcha  When `href` is set, the navigation happens synchronously inside the click handler. `ry:click` listeners can call `event.detail.originalEvent.preventDefault()`, but that does NOT block the href navigation — it only blocks the default browser click action (which doesn't apply to a non-anchor host). For cancel-friendly navigation, use a real `<a>` inside the card and let it own the href.
 *
 * @gotcha  `interactive` without `href` is valid — the card fires `ry:click` but does not navigate. Use this when the listener implements the action (open a modal, drill into a detail view via router, etc.).
 *
 * @example basic
 * ```html
 * <ry-card>
 *   <h3>Card title</h3>
 *   <p>Plain content card with no interactive behavior.</p>
 * </ry-card>
 * ```
 *
 * @example with-image
 * ```html
 * <ry-card>
 *   <img src="/cover.jpg" alt="">
 *   <h3>Trip to Tokyo</h3>
 *   <p>Cherry blossom season, 2026.</p>
 * </ry-card>
 * ```
 *
 * @example interactive-href
 * ```html
 * <ry-card interactive href="/demos/goap">
 *   <h3>GOAP Demo</h3>
 *   <p>Click anywhere on the card to open the demo.</p>
 * </ry-card>
 * ```
 *
 * @example interactive-no-href
 * ```html
 * <ry-card interactive>
 *   <h3>Custom action</h3>
 *   <p>Listen for ry:click on this card to handle the click yourself.</p>
 * </ry-card>
 * ```
 *
 * @example with-inner-button
 * ```html
 * <ry-card interactive>
 *   <h3>Buy a thing</h3>
 *   <p>This card is clickable. The button below is also clickable — its click is independent and does not fire the card's ry:click.</p>
 *   <ry-button variant="primary">Buy</ry-button>
 * </ry-card>
 * ```
 *
 * @tutorial inert-by-default
 * ```js
 * // Wrong assumption: ry-card always emits ry:click. It does NOT — by
 * // default the element is purely a styling hook with no listeners.
 * const el = mount('<ry-card>x</ry-card>');
 * await expectEqual(el.getAttribute('role'), null, 'no role set by default');
 * await expectEqual(el.getAttribute('tabindex'), null, 'no tabindex set by default');
 * const fired = [];
 * el.addEventListener('ry:click', () => fired.push(1));
 * el.click();
 * await expectEqual(fired.length, 0, 'no ry:click on non-interactive card');
 * ```
 *
 * @tutorial interactive-sets-aria-and-tabindex
 * ```js
 * const el = mount('<ry-card interactive>x</ry-card>');
 * await expectAttr(el, 'role', 'link', 'role="link" when interactive');
 * await expectAttr(el, 'tabindex', '0', 'tabindex="0" when interactive');
 * ```
 *
 * @tutorial interactive-respects-explicit-tabindex
 * ```js
 * // Wrong assumption: interactive always overrides tabindex. It does NOT —
 * // a consumer-set tabindex is preserved.
 * const el = mount('<ry-card interactive tabindex="2">x</ry-card>');
 * await expectAttr(el, 'tabindex', '2', 'explicit tabindex survives setup');
 * ```
 *
 * @tutorial interactive-click-fires-ry-click
 * ```js
 * const el = mount('<ry-card interactive>x</ry-card>');
 * await expectEvent(el, 'ry:click', () => el.click());
 * ```
 *
 * @tutorial inner-button-click-suppresses-card-click
 * ```js
 * // Wrong assumption: every click inside an interactive card fires ry:click.
 * // Clicks on inner <a>, <button>, or <ry-button> are EXCLUDED so nested
 * // controls keep their semantics.
 * const el = mount(`
 *   <ry-card interactive>
 *     <button id="b">Buy</button>
 *   </ry-card>
 * `);
 * const fired = [];
 * el.addEventListener('ry:click', () => fired.push(1));
 * el.querySelector('#b').click();
 * await expectEqual(fired.length, 0, 'inner button click suppressed at the card level');
 * ```
 *
 * @tutorial keyboard-activates-when-interactive
 * ```js
 * const el = mount('<ry-card interactive>x</ry-card>');
 * const fired = [];
 * el.addEventListener('ry:click', () => fired.push(1));
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(fired.length, 1, 'Enter activates');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: ' ', bubbles: true }));
 * await expectEqual(fired.length, 2, 'Space activates');
 * ```
 *
 * @tutorial keyboard-noop-when-not-interactive
 * ```js
 * const el = mount('<ry-card>x</ry-card>');
 * const fired = [];
 * el.addEventListener('ry:click', () => fired.push(1));
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(fired.length, 0, 'Enter ignored on non-interactive card');
 * ```
 *
 * @see ry-button
 * @see ry-stat
 */

import { RyElement } from '../core/ry-element.js';

export class RyCard extends RyElement {
  setup(): void {
    if (!this.hasAttribute('interactive')) return;

    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
    this.setAttribute('role', 'link');

    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
  }

  #handleClick = (e: MouseEvent): void => {
    // Ignore clicks on links/buttons inside the card
    const target = e.target as HTMLElement;
    if (target.closest('a, button, ry-button')) return;

    const href = this.getAttribute('href');
    if (href) {
      window.location.href = href;
    }
    this.emit('click', { originalEvent: e });
  };

  #handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.click();
    }
  };
}

customElements.define('ry-card', RyCard);
